import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import {
  Sun, Moon, Monitor, Check, Lock, Link2, CalendarCheck2,
  Eye, EyeOff, Compass, ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  apiChangePassword,
  apiGetCalendarStatus,
  apiDisconnectCalendar,
  getCalendarOAuthUrl,
} from "@/lib/api";
import MobileLayout from "./MobileLayout";

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
        {title}
      </p>
      <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
        {children}
      </div>
    </div>
  );
}

// ── Appearance ────────────────────────────────────────────────────────────────
function AppearanceSection() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const options = [
    { value: "light",  labelKey: "mobile.settings.light",  icon: Sun },
    { value: "dark",   labelKey: "mobile.settings.dark",   icon: Moon },
    { value: "system", labelKey: "mobile.settings.system", icon: Monitor },
  ] as const;

  return (
    <Section title={t("mobile.settings.appearance")}>
      <div className="grid grid-cols-3 divide-x divide-border/40">
        {options.map(({ value, labelKey, icon: Icon }) => {
          const active = (theme ?? "system") === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setTheme(value)}
              className={`flex flex-col items-center gap-1.5 py-4 transition-colors ${
                active ? "bg-primary/5" : "hover:bg-muted/40 active:bg-muted/60"
              }`}
            >
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                active ? "bg-primary/15" : "bg-muted/60"
              }`}>
                <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
              </div>
              <span className={`text-xs font-medium ${active ? "text-primary" : "text-foreground"}`}>
                {t(labelKey)}
              </span>
              {active && (
                <span className="h-1 w-4 rounded-full bg-primary" />
              )}
            </button>
          );
        })}
      </div>
    </Section>
  );
}

// ── Language ──────────────────────────────────────────────────────────────────
function LanguageSection() {
  const { t, i18n } = useTranslation();
  const languages = [
    { code: "en", label: "English",    flag: "🇬🇧", desc: "English (UK)" },
    { code: "mk", label: "Македонски", flag: "🇲🇰", desc: "Македонски"   },
    { code: "sr", label: "Српски",     flag: "🇷🇸", desc: "Srpski"       },
  ];

  function changeLang(code: string) {
    i18n.changeLanguage(code);
    localStorage.setItem("besthr_lang", code);
  }

  // Use resolvedLanguage which normalises "en-US" → "en"
  const activeLang = i18n.resolvedLanguage ?? i18n.language;

  return (
    <Section title={t("mobile.settings.language")}>
      {languages.map(({ code, label, flag, desc }, i) => {
        const active = activeLang === code || activeLang.startsWith(code + "-");
        return (
          <button
            key={code}
            type="button"
            onClick={() => changeLang(code)}
            className={`w-full flex items-center gap-3 px-4 py-3.5 transition-colors ${
              i < languages.length - 1 ? "border-b border-border/30" : ""
            } ${active ? "bg-primary/5" : "hover:bg-muted/40 active:bg-muted/60"}`}
          >
            <span className="text-xl shrink-0">{flag}</span>
            <div className="flex-1 text-left">
              <p className={`text-sm font-medium ${active ? "text-primary" : "text-foreground"}`}>
                {label}
              </p>
              <p className="text-[11px] text-muted-foreground">{desc}</p>
            </div>
            {active && (
              <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                <Check className="h-3 w-3 text-primary-foreground" />
              </div>
            )}
          </button>
        );
      })}
    </Section>
  );
}

// ── Calendar connection ───────────────────────────────────────────────────────
function CalendarSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: status, isLoading } = useQuery({
    queryKey: ["calendar-status"],
    queryFn: apiGetCalendarStatus,
  });

  // Show success toast when returning from OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("calendarConnected") === "1") {
      toast.success(t("mobile.settings.calendarConnectedSuccess"));
      const url = new URL(window.location.href);
      url.searchParams.delete("calendarConnected");
      url.searchParams.delete("provider");
      window.history.replaceState({}, "", url.toString());
      queryClient.invalidateQueries({ queryKey: ["calendar-status"] });
    }
  }, [queryClient, t]);

  const disconnect = useMutation({
    mutationFn: apiDisconnectCalendar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-status"] });
      toast.success(t("mobile.settings.calendarDisconnected"));
    },
    onError: () => toast.error(t("mobile.settings.calendarDisconnectFailed")),
  });

  return (
    <Section title={t("mobile.settings.calendarConnection")}>
      {isLoading ? (
        <div className="px-4 py-4 text-sm text-muted-foreground">{t("mobile.settings.checkingConnection")}</div>
      ) : status?.connected ? (
        <div className="px-4 py-4 space-y-3">
          <div className="flex items-center gap-3 rounded-xl bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 px-3 py-2.5">
            <Link2 className="h-4 w-4 text-green-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-700 dark:text-green-400 capitalize">
                {status.provider} {t("mobile.settings.connected")}
              </p>
              {status.connectedAt && (
                <p className="text-[11px] text-muted-foreground">
                  {t("mobile.settings.since")} {new Date(status.connectedAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full h-10 text-sm"
            onClick={() => disconnect.mutate()}
            disabled={disconnect.isPending}
          >
            {disconnect.isPending ? t("mobile.settings.disconnecting") : t("mobile.settings.disconnectCalendar")}
          </Button>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          <p className="text-xs text-muted-foreground">{t("mobile.settings.connectCalendarHint")}</p>
          <div className="grid grid-cols-2 gap-2">
            <a
              href={getCalendarOAuthUrl("microsoft", "/mobile/settings")}
              className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              {/* Microsoft logo */}
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="10" height="10" fill="#F25022"/>
                <rect x="11" width="10" height="10" fill="#7FBA00"/>
                <rect y="11" width="10" height="10" fill="#00A4EF"/>
                <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
              </svg>
              Microsoft
            </a>
            <a
              href={getCalendarOAuthUrl("google", "/mobile/settings")}
              className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              {/* Google logo */}
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </a>
          </div>
        </div>
      )}
    </Section>
  );
}

// ── Password change ───────────────────────────────────────────────────────────
function PasswordSection() {
  const { t } = useTranslation();
  const [current, setCurrent]   = useState("");
  const [next, setNext]         = useState("");
  const [confirm, setConfirm]   = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext]       = useState(false);
  const [error, setError]       = useState("");
  const [saving, setSaving]     = useState(false);
  const [success, setSuccess]   = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!current) { setError(t("mobile.settings.passwordRequired")); return; }
    if (next.length < 8) { setError(t("mobile.settings.passwordMinLength")); return; }
    if (next !== confirm) { setError(t("mobile.settings.passwordMismatch")); return; }
    setSaving(true);
    try {
      await apiChangePassword({ currentPassword: current, newPassword: next });
      setSuccess(true);
      setCurrent(""); setNext(""); setConfirm("");
      toast.success(t("mobile.settings.passwordSuccess"));
    } catch (e: any) {
      setError(e?.message ?? t("mobile.settings.passwordFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title={t("mobile.settings.security")}>
      <div className="px-4 py-4">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">{t("mobile.settings.changePassword")}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("mobile.settings.currentPassword")}</Label>
            <div className="relative">
              <Input
                type={showCurrent ? "text" : "password"}
                placeholder="••••••••"
                value={current}
                onChange={(e) => { setCurrent(e.target.value); setSuccess(false); setError(""); }}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("mobile.settings.newPassword")}</Label>
            <div className="relative">
              <Input
                type={showNext ? "text" : "password"}
                placeholder={t("mobile.settings.newPasswordMin")}
                value={next}
                onChange={(e) => { setNext(e.target.value); setSuccess(false); setError(""); }}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNext((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showNext ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("mobile.settings.confirmNewPassword")}</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setSuccess(false); setError(""); }}
            />
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          {success && <p className="text-xs text-emerald-600">{t("mobile.settings.passwordSuccess")} ✓</p>}
          <Button
            type="submit"
            className="w-full h-10 gradient-brand text-primary-foreground"
            disabled={saving}
          >
            {saving ? t("mobile.settings.saving") : t("mobile.settings.updatePassword")}
          </Button>
        </form>
      </div>
    </Section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
// ── Take a tour ─────────────────────────────────────────────────────────────
function TourSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const startTour = () => {
    // Flag is consumed by MobileProductTour once it's mounted on /mobile.
    try { sessionStorage.setItem("besthr_start_mobile_tour", "1"); } catch {}
    navigate("/mobile");
  };
  return (
    <Section title={t("mobile.settings.help", "Help")}>
      <button
        type="button"
        onClick={startTour}
        className="w-full flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-muted/40 active:bg-muted/60"
      >
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Compass className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-foreground">
            {t("mobile.settings.takeTour", "Take a tour")}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {t("mobile.settings.takeTourDesc", "A quick walkthrough of the app.")}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </button>
    </Section>
  );
}

export default function MobileSettings() {
  const { t } = useTranslation();
  return (
    <MobileLayout title={t("mobile.settings.title")} showBack>
      <div className="px-4 py-4 space-y-5 pb-8">
        <AppearanceSection />
        <LanguageSection />
        <TourSection />
        <CalendarSection />
        <PasswordSection />
      </div>
    </MobileLayout>
  );
}
